import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";

export default function ConsistencyCheckerPanel({ projectId }) {
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/sync/last-report`);
      if (data && data.report) setReport(data);
    } catch { /* empty */ }
  };

  useEffect(() => { load(); }, [projectId]);

  const runCheck = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/sync/check`, {});
      setReport(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  // ── File 53: Topology coverage check ──
  const [topology, setTopology] = useState(null);
  const runTopology = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/projects/${projectId}/pid-3d-sync`);
      setTopology(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const rows = (report?.report || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "issues") return r.issue !== null;
    return r.kind === filter;
  });

  const summary = report?.summary || { total: 0, ok: 0, issues: 0, by_kind: {} };

  return (
    <div data-testid="sync-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ ...label, marginBottom: 2 }}>P&ID ↔ 3D Consistency</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Compares equipment / lines / instruments / endpoint connections between P&ID and 3D model.
          </div>
        </div>
        <button data-testid="sync-run" style={btn} onClick={runCheck} disabled={busy}>
          {busy ? "Checking…" : "Run Consistency Check"}
        </button>
        <button data-testid="sync-topology-btn"
                 style={{ ...btn, background: "#16a34a", borderColor: "#16a34a" }}
                 onClick={runTopology} disabled={busy}>
          Check P&ID-3D Coverage
        </button>
      </div>

      {topology && topology.coverage && (
        <div data-testid="sync-topology-result" style={{ ...card }}>
          <div style={{ ...label, marginBottom: 8 }}>
            Topology Coverage —{" "}
            <span style={{
              color: topology.coverage.status === "COMPLETE" ? "#16a34a"
                   : topology.coverage.status === "PARTIAL" ? "#FFB000"
                   : "#dc2626",
            }}>
              {topology.coverage.status} · {topology.coverage.coverage_pct}%
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: "var(--pf-muted)" }}>P&ID equipment</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{topology.topology.total_equipment}</div>
            </div>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: "var(--pf-muted)" }}>P&ID connections</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{topology.coverage.pid_connections}</div>
            </div>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: "var(--pf-muted)" }}>3D pipes</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{topology.coverage.model_pipes}</div>
            </div>
            <div style={{ fontSize: 11 }}>
              <div style={{ color: "var(--pf-muted)" }}>Matched</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#16a34a" }}>{topology.coverage.matched}</div>
            </div>
          </div>
          {(topology.coverage.missing_equipment_in_3d?.length > 0
            || topology.coverage.missing_pipes_in_3d?.length > 0) && (
            <div style={{ fontSize: 11, padding: 8, background: "rgba(220,38,38,0.08)", borderRadius: 4, color: "#FCA5A5" }}>
              <strong>Missing in 3D:</strong>{" "}
              {topology.coverage.missing_equipment_in_3d?.length || 0} equipment ·{" "}
              {topology.coverage.missing_pipes_in_3d?.length || 0} pipes
              {topology.coverage.missing_equipment_in_3d?.length > 0 && (
                <div style={{ marginTop: 4, fontFamily: "monospace" }}>
                  Eq: {topology.coverage.missing_equipment_in_3d.slice(0, 8).join(", ")}
                  {topology.coverage.missing_equipment_in_3d.length > 8 && " …"}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: 10, color: "var(--pf-muted)", marginTop: 8 }}>
            {topology.coverage.action}
          </div>
        </div>
      )}

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      {report && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
            <Kpi label="Total checked" value={summary.total} testid="sync-kpi-total" />
            <Kpi label="OK" value={summary.ok} color="#22c55e" testid="sync-kpi-ok" />
            <Kpi label="Issues" value={summary.issues} color="#ef4444" testid="sync-kpi-issues" />
            <Kpi label="Equipment" value={summary.by_kind?.equipment || 0} testid="sync-kpi-eq" />
            <Kpi label="Lines" value={summary.by_kind?.line || 0} testid="sync-kpi-lines" />
            <Kpi label="Loops" value={summary.by_kind?.control_loop || 0} testid="sync-kpi-loops" />
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["all", "All"], ["issues", "Issues only"], ["equipment", "Equipment"],
              ["line", "Lines"], ["connection", "Broken endpoints"], ["control_loop", "Control loops"]].map(([k, lbl]) => (
              <button key={k} data-testid={`sync-filter-${k}`}
                style={filter === k ? btn : btnGhost} onClick={() => setFilter(k)}>{lbl}</button>
            ))}
          </div>

          <div style={card}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "var(--pf-surface-2)" }}>
                    {["Item", "Kind", "P&ID", "3D Model", "Issue", "Action"].map((h) =>
                      <th key={h} style={{ padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }} data-testid={`sync-row-${i}`}>
                      <td style={{ padding: 6, fontWeight: 700, color: "#F26B1F" }}>{r.item}</td>
                      <td style={{ padding: 6 }}>{r.kind}</td>
                      <td style={{ padding: 6 }}><StatusBadge s={r.pid_status} /></td>
                      <td style={{ padding: 6 }}><StatusBadge s={r.model_status} /></td>
                      <td style={{ padding: 6, color: r.issue ? "#ef4444" : "var(--pf-muted)" }}>{r.issue || "—"}</td>
                      <td style={{ padding: 6, fontSize: 10 }}>{r.action || "—"}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan="6" style={{ padding: 16, textAlign: "center", color: "var(--pf-muted)" }}>
                      No rows match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!report && !busy && (
        <div style={{ ...card, textAlign: "center", color: "var(--pf-muted)", padding: 24 }}>
          No consistency report yet — click Run.
        </div>
      )}
    </div>
  );
}

function Kpi({ label: l, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ padding: 10, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 6 }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || "#F26B1F", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = { OK: "#22c55e", MISSING: "#ef4444", BROKEN: "#ef4444", INCOMPLETE: "#f59e0b", "—": "var(--pf-muted)" };
  return <span style={{ fontFamily: "monospace", fontSize: 10, color: map[s] || "var(--pf-fg)" }}>{s || "—"}</span>;
}
